import { IEntityChildren, IApp03Task } from '../types';
import { EntityStatus } from '../refData/EntityStatus';
import { AppGlobals } from '../AppGlobals';
import { EntityService } from './EntityService';
import { WorkflowType } from '../refData/WorkflowType';
import { SystemStages } from '../types/App03WorkflowStage';
import { App03Frameworks } from '../types/App03Workflow';
import { ReorderTaskPayload } from '../components/app03Workflows/App03TaskBoard';

export class App03TaskService extends EntityService<IApp03Task> {
    private apiURL: string;
    protected childrenEntities = ['App03TaskUsers($expand=User)', 'App03TaskObservers($expand=User)', 'CustomFields($expand=CustomFieldDfn)',];

    constructor() {
        super(`/App03Tasks`);
        const apiURL = process.env.REACT_APP_ZedX_API!;
        this.apiURL = apiURL;
    }

    public readAllForTasksList(
        frameworkID: number,
        workflowID: number,
        workflowStageID: number,
        archived: boolean,
        userID: number,
        sprintID: number,
        epicID: number
    ): Promise<IApp03Task[]> {

        const sprintFilter =
            frameworkID === App03Frameworks.Scrum &&
                sprintID != null &&
                archived !== true
                ? ` and (
                    WorkflowStage/SystemStage eq 'Backlog'
                    or (WorkflowStage/SystemStage ne 'Backlog' and SprintID eq ${sprintID})
                )`
                : ``;

        return this.readAll(
            `?$select=ID,Title,TaskRef,ExternalRef,TaskPriorityID,Archived,TargetStartDate,TargetEndDate`
            + `&$orderby=TaskOrder`
            + `&$expand=Workflow($select=RefColumnID),`
            + `App03TaskUsers($expand=User($select=Title)),`
            + `WorkflowStage($select=SystemStage)`
            + `&$filter=WorkflowID eq ${workflowID}`
            + (userID != null ? ` and App03TaskUsers/any(taskUser: taskUser/UserID eq ${userID})` : ``)
            + (workflowStageID != null
                ? ` and WorkflowStageID eq ${workflowStageID} and Archived ne true`
                : ``)
            + (archived != null ? ` and Archived eq ${archived}` : ``)
            + sprintFilter
            + (epicID != null ? ` and EpicID eq ${epicID}` : ``)
        );
    }


    public readAllForStagesTasksList(
        frameworkID: number,
        workflowID: number,
        workflowStageIDs: number[],
        archived: boolean,
        userID: number,
        sprintID: number,
        epicID: number
    ): Promise<IApp03Task[]> {

        const stagesFilter = workflowStageIDs.length > 0
            ? ` and WorkflowStageID in (${workflowStageIDs.join(",")})`
            : "";

        const sprintFilter =
            frameworkID === App03Frameworks.Scrum &&
                sprintID != null &&
                archived !== true
                ? ` and (
                    WorkflowStage/SystemStage eq 'Backlog'
                    or (WorkflowStage/SystemStage ne 'Backlog' and SprintID eq ${sprintID})
                )`
                : "";

        return this.readAll(
            `?$select=ID,Title,WorkflowStageID,TaskRef,ExternalRef,TaskPriorityID,TaskTimeOption,PlannedTime,Archived,TargetStartDate,TargetEndDate`
            + `&$orderby=TaskOrder`
            + `&$expand=Workflow($select=RefColumnID),Epic($select=ID,Title,EpicRef),`
            + `App03TaskUsers($expand=User($select=Title)),`
            + `WorkflowStage($select=SystemStage)`
            + `&$filter=WorkflowID eq ${workflowID}`
            + (userID != null ? ` and App03TaskUsers/any(taskUser: taskUser/UserID eq ${userID})` : ``)
            + stagesFilter
            + (archived === true
                ? ` and Archived eq true`
                : ` and Archived ne true`)
            + sprintFilter
            + (epicID != null ? ` and EpicID eq ${epicID}` : ``)
        );
    }




    public readAllForStageCounters(workflowID?: number): Promise<IApp03Task[]> {
        return this.readAll(
            `?$select=ID,Title,WorkflowStageID,TaskPriorityID,Archived,ProjectID,UpdatePeriod,SprintID,EpicID`
            + `&$orderby=Title`
            + `&$expand=App03TaskUsers($select=ID,UserID)`
            + `&$filter=WorkflowID eq ${workflowID}`
        );
    }

    public readAllActiveTasksForCounters(frameworkID: App03Frameworks): Promise<IApp03Task[]> {
        return this.readAll(
            `?$select=ID,TaskPriorityID,WorkflowID`
            + `&$orderby=ID`
            + `&$expand=App03TaskUsers($select=UserID)`
            + `,Workflow($select=App03PortfolioID,EntityStatusID,FrameworkID;$expand=App03WorkflowUsers($select=UserID))`
            + `,WorkflowStage($select=SystemStage)` // expand WorkflowStage to access SystemStage
            + `&$filter=Archived ne true`
            + ` and Workflow/EntityStatusID eq ${EntityStatus.Open}`
            + ` and WorkflowStage/SystemStage eq '${SystemStages.Active}'` // only Active stage tasks
            + ` and Workflow/FrameworkID eq ${frameworkID}`
        );
    }



    public readAllForListStudyPlanner(includeClosed?: boolean, workflowStageTitle?: string, archived?: boolean, userID?: number): Promise<IApp03Task[]> {
        return this.readAll(
            `?$select=ID,Title,TaskRef,ExternalRef,TaskPriorityID,Archived,TargetStartDate,TargetEndDate`
            + `&$orderby=TaskOrder`
            + `&$expand=WorkflowStage($select=Title),Workflow($select=Title,RefColumnID),TaskManagerUser($select=Title),`
            + `App03TaskUsers($expand=User($select=Title))`
            + `&$filter=ID gt 0`
            + ` and Workflow/WorkflowTypeID eq ${WorkflowType.StudyPlanner}`
            + (userID !== undefined ? ` and App03TaskUsers/any(taskUser: taskUser/UserID eq ${userID})` : ``)
            + (workflowStageTitle ? ` and WorkflowStage/Title eq '${workflowStageTitle}' and Archived ne true` : ``)
            + (archived !== undefined ? ` and Archived eq ${archived}` : ``)
        );
    }

    public countTasksForStudyPlannerStudent(userID?: number): Promise<IApp03Task[]> {
        return this.readAll(
            `?$select=ID,Archived`
            + `&$expand=WorkflowStage($select=Title)`
            + `&$filter=Workflow/WorkflowTypeID eq ${WorkflowType.StudyPlanner}`
            + (userID !== undefined ? ` and App03TaskUsers/any(taskUser: taskUser/UserID eq ${userID})` : ``)
        );
    }


    public async readForView(taskID: number): Promise<IApp03Task> {
        return this.read(
            taskID,
            false,
            false,
            [
                'App03TaskUsers($expand=User($select=Title,EmailAddress))',
                'WorkflowStage($select=ID,Title)',
                'AddedBy($select=ID,Title)',
            ]
        );
    }

    public async readForViewStudyPlanner(taskID: number): Promise<IApp03Task> {
        return this.read(
            taskID,
            false,
            false,
            [
                'TaskManagerUser($select=Title,EmailAddress)',
                'WorkflowStage($select=ID,Title)',
                'AddedBy($select=ID,Title)',
            ]
        );
    }

    public async reorderTask(payload: ReorderTaskPayload): Promise<void> {
        await this.axiosPost(`${this.apiURL}/ReorderTask`, payload);
    }

    public async updateTaskPriority(payload: {
        TaskID: number;
        TaskPriorityID: number;
    }): Promise<void> {
        await this.axiosPost(`${this.apiURL}/UpdateTaskPriority`, payload);
    }

    public async entityChildren(id: number): Promise<IEntityChildren[]> {

        const entityUrl = `${this.entityUrl}(${id})`;

        const evidences = (
            await this.axiosGet(
                `${entityUrl}/App03TaskEvidences?$select=ID&$top=10`
            )
        ).data.value;

        const subTasks = (
            await this.axiosGet(
                `${entityUrl}/App03SubTasks?$select=ID&$top=10`
            )
        ).data.value;

        return [
            {
                ChildType: 'Work Item Discussions',
                CanBeAdopted: false,
                ChildIDs: evidences.map((m: { ID: number }) => m.ID)
            },
            {
                ChildType: 'Sub Work Items',
                CanBeAdopted: false,
                ChildIDs: subTasks.map((m: { ID: number }) => m.ID)
            }
        ];
    }


}